import React, { useState, useCallback } from "react";
import RtlsMap from "./components/rtlsMap";
import Modal from "./components/Modal";
import PersonnelActivityHistory from "./components/modalContent/personnelMetricsModalComponents/personnelActivityHistory";
import ShiftSummary from "./components/modalContent/personnelMetricsModalComponents/shiftSummary";
import { usePersonnelData } from "./hooks/usePersonnelData";
import { useIsMobile } from "./hooks/useIsMobile";
import DisabledTooltip from "./components/disabledToolTip";
import "./personnelMetrics.css";
import "../animation.css";

const FUNCTION_BUTTONS = [
  {
    id: "heatmap",
    label: "Heatmap",
    subtitle: "RTLS API",
    rtls: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="3" />
        <circle cx="12" cy="12" r="7" opacity="0.5" />
        <circle cx="12" cy="12" r="10" opacity="0.25" />
      </svg>
    ),
  },
  {
    id: "spaghetti",
    label: "Spaghetti Map",
    subtitle: "RTLS API",
    rtls: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M3 12c0-4 2-8 6-8s6 4 6 8-2 8-6 8" />
        <path d="M15 4c4 0 6 4 6 8s-2 8-6 8" />
      </svg>
    ),
  },
  // ── NEW: Workflow Map - row 1 slot 3, matching moduleMetricsPage.js ──
  {
    id: "workflow",
    label: "Workflow Map",
    subtitle: "RTLS API",
    rtls: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x="3" y="3" width="5" height="5" rx="1" />
        <rect x="16" y="3" width="5" height="5" rx="1" />
        <rect x="3" y="16" width="5" height="5" rx="1" />
        <rect x="16" y="16" width="5" height="5" rx="1" />
        <line x1="8" y1="5.5" x2="16" y2="5.5" />
        <line x1="5.5" y1="8" x2="5.5" y2="16" />
        <line x1="18.5" y1="8" x2="18.5" y2="16" />
        <line x1="8" y1="18.5" x2="16" y2="18.5" />
      </svg>
    ),
  },
  // ── Playback moves to row 2 slot 1 ──
  {
    id: "playback",
    label: "Playback",
    subtitle: "RTLS API",
    rtls: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    ),
  },
  {
    id: "activity",
    label: "Activity History",
    subtitle: "SQL / D365",
    modal: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
  {
    id: "shift",
    label: "Shift Summary",
    subtitle: "SQL / D365",
    modal: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <line x1="8" y1="14" x2="10" y2="14" />
        <line x1="12" y1="14" x2="16" y2="14" />
        <line x1="8" y1="18" x2="10" y2="18" />
      </svg>
    ),
  },
];
const getBatteryHealth = (voltage) => {
  if (voltage == null || isNaN(voltage))
    return {
      label: "Unknown",
      color: "var(--pm-muted)",
      bg: "var(--pm-light)",
      border: "var(--pm-border)",
    };

  const v = parseFloat(voltage);

  if (v > 3.0) {
    return {
      label: "Full",
      color: "var(--pm-green)",
      bg: "rgba(56,161,105,0.12)",
      border: "rgba(56,161,105,0.3)",
    };
  } else if (v >= 2.9) {
    return {
      label: "High",
      color: "var(--pm-green)",
      bg: "rgba(56,161,105,0.12)",
      border: "rgba(56,161,105,0.3)",
    };
  } else if (v >= 2.6) {
    return {
      label: "Medium",
      color: "var(--pm-amber)",
      bg: "rgba(214,158,46,0.12)",
      border: "rgba(214,158,46,0.3)",
    };
  } else if (v >= 2.5) {
    return {
      label: "Low",
      color: "#ed8936", // Orange
      bg: "rgba(237,137,54,0.12)",
      border: "rgba(237,137,54,0.3)",
    };
  } else {
    return {
      label: "Empty",
      color: "var(--pm-red)",
      bg: "rgba(204,0,0,0.10)",
      border: "rgba(204,0,0,0.25)",
    };
  }
};

const getBatteryBarWidth = (voltage) => {
  if (voltage == null || isNaN(voltage)) return 0;
  const v = parseFloat(voltage);

  // Map voltage to percentage (2.5V = 0%, 3.0V = 100%)
  const minV = 2.5;
  const maxV = 3.0;
  const percentage = Math.max(
    0,
    Math.min(100, ((v - minV) / (maxV - minV)) * 100),
  );
  return percentage;
};
const PersonnelMetricsPage = () => {
  const {
    search,
    setSearch,
    dropdownOpen,
    setDropdownOpen,
    dropdownPersonnel,
    selectedPerson,
    handleSelectPerson,
    handleClearSelection,
    personnelData,
    metricsLoading,
    metricsError,
    listLoading,
    listError,
  } = usePersonnelData();

  const [activeModal, setActiveModal] = useState(null);
  const [activeRtlsFn, setActiveRtlsFn] = useState(null);

  const isMobile = useIsMobile();
  const visibleButtons = isMobile
    ? FUNCTION_BUTTONS.filter((btn) => btn.modal)
    : FUNCTION_BUTTONS;

  // ── handleFunctionBtn ──────────────────────────────────────────────────────
  const handleFunctionBtn = useCallback(
    (btnId) => {
      if (!selectedPerson) return;

      if (btnId === "activity" || btnId === "shift") {
        setActiveModal(btnId);
        return;
      }

      if (FUNCTION_BUTTONS.find((b) => b.id === btnId)?.rtls) {
        // Toggle: same → close, different → swap
        setActiveRtlsFn((prev) => (prev === btnId ? null : btnId));
      }
    },
    [selectedPerson],
  );

  // Wrap hook's handleSelectPerson to also close the RTLS filter popup
  const onSelectPerson = useCallback(
    (person) => {
      handleSelectPerson(person);
      setActiveRtlsFn(null);
    },
    [handleSelectPerson],
  );

  // Wrap hook's handleClearSelection to also close the RTLS filter popup
  const onClearSelection = useCallback(() => {
    handleClearSelection();
    setActiveRtlsFn(null);
  }, [handleClearSelection]);

  // Called by RtlsMap when popup is dismissed (Cancel / ✕ / ESC)
  const handleRtlsFnClose = useCallback(() => {
    setActiveRtlsFn(null);
  }, []);

  // Called by RtlsMap after Apply - popup stays open, this is notification only
  const handleRtlsFilterApply = useCallback((filters) => {
    console.log("[PersonnelMetrics] RTLS filter applied:", filters);
    // filters = { fn: "heatmap"|..., beginTs, endTs, objectName?, label? }
  }, []);

  const personnelFilterQuery = useCallback(() => {
    if (!selectedPerson) return null;
    return `{obj.name} = "${selectedPerson.name}"`;
  }, [selectedPerson]);

  const closeModal = useCallback(() => setActiveModal(null), []);

  // ── Derived display values from live personnelData ─────────────────────────
  // personnelData is null until the metrics fetch resolves; fall back to 0 so
  // battery helpers always receive a number.
  const voltage = personnelData?.tagBatteryLevel;
  const batteryHealth = getBatteryHealth(voltage);
  const batteryBarWidth = getBatteryBarWidth(voltage);

  const jobsColor =
    personnelData && personnelData.timeInIndirect >= 5
      ? "var(--pm-amber)"
      : "var(--pm-green)";

  return (
    <div className="pm-page page-enter">
      <div className="pm-page-header">
        <h1>Personnel Metrics</h1>
        <p className="pm-page-subtitle">
          Search and inspect individual personnel location and production data
        </p>
      </div>

      <div className="pm-layout">
        {!isMobile && (
          <div className="pm-map-col">
            <RtlsMap
              isHomePage={false}
              filterQuery={personnelFilterQuery()}
              activeRtlsFn={activeRtlsFn}
              rtlsSelectedObject={selectedPerson?.name ?? null}
              onRtlsFilterApply={handleRtlsFilterApply}
              onRtlsFnClose={handleRtlsFnClose}
            />
          </div>
        )}

        <div className="pm-right-col">
          <div
            className="pm-card pm-search-card stagger-item"
            style={{ "--i": 0 }}
          >
            <div className="pm-search-wrap">
              <div className="pm-search-icon">
                {listLoading ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="pm-search-spinner"
                  >
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                )}
              </div>
              <input
                className="pm-search-input"
                type="text"
                placeholder={
                  listLoading
                    ? "Loading personnel…"
                    : listError
                      ? "Personnel list unavailable"
                      : "Search by name, ID, or department…"
                }
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                disabled={listLoading || !!listError}
                aria-label="Search personnel"
              />
              {search && (
                <button
                  className="pm-search-clear"
                  onClick={onClearSelection}
                  aria-label="Clear search"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>

            {listError && (
              <div className="pm-list-error">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {listError}
              </div>
            )}

            {dropdownOpen && dropdownPersonnel.length > 0 && (
              <ul className="pm-dropdown" role="listbox">
                {dropdownPersonnel.map((p) => (
                  <li
                    key={p.employeeNumber ?? p.objId}
                    className="pm-dropdown-item"
                    onMouseDown={() => onSelectPerson(p)}
                    role="option"
                  >
                    <span className="pm-dropdown-name">
                      {p.name ?? p.employeeNumber}
                    </span>
                    <span className="pm-dropdown-meta">
                      {p.employeeNumber} &bull; {p.department ?? "-"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {dropdownOpen &&
              search.trim() &&
              dropdownPersonnel.length === 0 && (
                <div className="pm-dropdown pm-dropdown--empty">
                  No personnel match your search.
                </div>
              )}
          </div>

          {!selectedPerson && (
            <div className="pm-empty-state stagger-item" style={{ "--i": 1 }}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <p>Search for a team member to view details</p>
              <span>Enter a full name above to load personnel data</span>
            </div>
          )}

          {selectedPerson && (
            <>
              {/* Section 1: Actions */}
              <div className="pm-card stagger-item" style={{ "--i": 1 }}>
                <h2 className="pm-section-title">Actions</h2>
                <div className="pm-fn-grid">
                  {visibleButtons.map((btn) => {
                    const isRtlsActive = btn.rtls && activeRtlsFn === btn.id;
                    return (
                      <DisabledTooltip
                        key={btn.id}
                        disabled={!btn.modal && !btn.rtls}
                      >
                        <button
                          className={`pm-fn-btn${btn.modal ? " pm-fn-btn--modal" : ""}${isRtlsActive ? " pm-fn-btn--active" : ""}`}
                          onClick={() => handleFunctionBtn(btn.id)}
                          title={btn.subtitle}
                        >
                          <div className="pm-fn-btn-icon">{btn.icon}</div>
                          <div className="pm-fn-btn-text">
                            <span className="pm-fn-btn-label">{btn.label}</span>
                            <span className="pm-fn-btn-sub">
                              {btn.subtitle}
                            </span>
                          </div>
                        </button>
                      </DisabledTooltip>
                    );
                  })}
                </div>
              </div>

              {/* Section 2: Personnel Information */}
              <div className="pm-card stagger-item" style={{ "--i": 2 }}>
                <h2 className="pm-section-title">Personnel Information</h2>

                {metricsLoading && (
                  <div className="pm-metrics-loading">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="pm-search-spinner"
                    >
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    <span>Loading personnel data…</span>
                  </div>
                )}

                {metricsError && !metricsLoading && (
                  <div className="pm-metrics-error">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {metricsError}
                  </div>
                )}

                {!metricsLoading && !metricsError && personnelData && (
                  <div className="pm-info-grid">
                    <div className="pm-info-item">
                      <span className="pm-info-label">Full Name</span>
                      <span className="pm-info-value">
                        {personnelData.fullName ?? "-"}
                      </span>
                    </div>
                    <div className="pm-info-item">
                      <span className="pm-info-label">Employee Number</span>
                      <span className="pm-info-value">
                        {personnelData.employeeNumber ?? "-"}
                      </span>
                    </div>
                    <div className="pm-info-item">
                      <span className="pm-info-label">Department</span>
                      <span className="pm-info-value">
                        {personnelData.department ?? "-"}
                      </span>
                    </div>
                    <div className="pm-info-item">
                      <span className="pm-info-label">Current Zone</span>
                      <span className="pm-info-value">
                        {personnelData.currentZone ?? "-"}
                      </span>
                    </div>
                    <div className="pm-info-item">
                      <span className="pm-info-label">Current Job</span>
                      <span className="pm-info-value">
                        {personnelData.currentJob ?? "-"}
                      </span>
                    </div>
                    <div className="pm-info-item">
                      <span className="pm-info-label">Last Updated</span>
                      <span className="pm-info-value">
                        {personnelData.lastUpdatedTime
                          ? new Date(
                              personnelData.lastUpdatedTime,
                            ).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-"}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 3: Personnel Metrics */}
              <div className="pm-card stagger-item" style={{ "--i": 3 }}>
                <h2 className="pm-section-title">Personnel Metrics</h2>

                {metricsLoading && (
                  <div className="pm-metrics-loading">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="pm-search-spinner"
                    >
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    <span>Loading metrics…</span>
                  </div>
                )}

                {metricsError && !metricsLoading && (
                  <div className="pm-metrics-error">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {metricsError}
                  </div>
                )}

                {!metricsLoading && !metricsError && personnelData && (
                  <div className="pm-metrics-grid">
                    {/* Tag Battery */}
                    <div className="pm-metric-tile">
                      <div className="pm-metric-tile-header">
                        <span className="pm-metric-label">Tag Battery</span>
                        <div className="pm-metric-tile-icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <rect x="2" y="7" width="18" height="10" rx="2" />
                            <path d="M22 11v2" strokeLinecap="round" />
                            <rect
                              x="4"
                              y="9"
                              width={`${Math.round(batteryBarWidth * 0.14)}`}
                              height="6"
                              rx="1"
                              fill="currentColor"
                              stroke="none"
                              style={{ color: batteryHealth.color }}
                            />
                          </svg>
                        </div>
                      </div>
                      <div
                        className="pm-metric-value-large"
                        style={{ color: batteryHealth.color }}
                      >
                        {voltage != null ? batteryHealth.label : "-"}
                      </div>
                      {voltage != null && (
                        <>
                          <div className="pm-battery-bar">
                            <div
                              className="pm-battery-fill"
                              style={{
                                width: `${batteryBarWidth}%`,
                                background: batteryHealth.color,
                              }}
                            />
                          </div>
                          <div className="pm-metric-label-sub">
                            {parseFloat(voltage).toFixed(2)}V
                          </div>
                        </>
                      )}
                    </div>

                    {/* Time Spent in Indirect */}
                    <div className="pm-metric-tile">
                      <div className="pm-metric-tile-header">
                        <span className="pm-metric-label">
                          Time Spent in Indirect
                        </span>
                        <div className="pm-metric-tile-icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>
                      </div>
                      <div
                        className="pm-metric-value-large"
                        style={{
                          color:
                            personnelData && personnelData.timeInIndirect >= 5
                              ? "var(--pm-amber)"
                              : "var(--pm-green)",
                        }}
                      >
                        {personnelData.timeInIndirect}
                      </div>
                      <div className="pm-metric-label-sub">Minutes</div>
                    </div>

                    {/* Total Movement */}
                    <div className="pm-metric-tile">
                      <div className="pm-metric-tile-header">
                        <span className="pm-metric-label">Total Movement</span>
                        <div className="pm-metric-tile-icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                        </div>
                      </div>
                      <div className="pm-metric-value-large">
                        {personnelData.totalMovement}
                        <span className="pm-metric-value-unit">m</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={activeModal === "activity"}
        onClose={closeModal}
        title="Activity History"
        size="large"
      >
        {selectedPerson && (
          <PersonnelActivityHistory
            person={selectedPerson}
            personnelData={personnelData}
          />
        )}
      </Modal>
      <Modal
        isOpen={activeModal === "shift"}
        onClose={closeModal}
        title="Shift Summary"
        size="medium"
      >
        {selectedPerson && (
          <ShiftSummary person={selectedPerson} personnelData={personnelData} />
        )}
      </Modal>
    </div>
  );
};

export default PersonnelMetricsPage;
